import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Typography, Button, CircularProgress,
  FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, FormHelperText
} from "@mui/material"
import { VisibilityOff, Visibility, Block, CheckCircleOutline } from '@mui/icons-material'

import { get, post } from '../utils/http'
import { isPrivate } from '../utils/config'
import logo from '../assets/pathomiq-logo.png'

interface FormValues {
  [key: string]: string,
  password: string,
  confirm_password: string,
}

const PasswordReset = () => {
  const [passForm, setPassForm] = useState<FormValues>({password: '', confirm_password: ''})
  const [formError, setFormError] = useState<FormValues>({password: '', confirm_password: ''})
  const [userId, setUserId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [formResMsg, setFormResMsg] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const [tokenCheck, setTokenCheck] = useState<boolean>(false)
  const [resetSuccess, setResetSuccess] = useState<boolean>(false)

  const { uniqueToken } = useParams()
  const minPassLength: number = 8

  const getUserInfo = async () => {
    try {
      const res = await get(`Users/bytoken/${uniqueToken}`)
      setUserId(res[0].id)
    } catch (err) {
      console.log(err)
    } finally {
      setTokenCheck(true)
    }
  }

  useEffect(() => {
    getUserInfo()
  }, [])  // eslint-disable-line

  const handleChange = (e: any) => {
    setFormResMsg('')
    setPassForm(prevForm => ({...prevForm, [e.target.name]: e.target.value}))
    setFormError(prevForm => ({...prevForm, [e.target.name]: ''}))
  }

  const handleClickShowPassword = () => setShowPassword(show => !show)
  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()
  const handleMouseUpPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()

  const handleClickShowConfirmPassword = () => setShowConfirmPassword(show => !show)
  const handleMouseDownConfirmPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()
  const handleMouseUpConfirmPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()

  const validateForm = () => {
    let valid = true

    // password check
    if (!passForm.password) {
      setFormError(prevForm => ({...prevForm, password: 'Password is required'}))
      valid = false
    } else if (passForm.password.length < minPassLength) {
      setFormError(prevForm => ({...prevForm, password: 'Your password must be at least 8 characters'}))
      valid = false
    } else setFormError(prevForm => ({...prevForm, password: ''}))
    // confirm password
    if (!passForm.confirm_password) {
      setFormError(prevForm => ({...prevForm, confirm_password: 'Confirm the password'}))
      valid = false
    } else if (passForm.confirm_password !== passForm.password) {
      setFormError(prevForm => ({...prevForm, confirm_password: 'Your password must match'}))
      valid = false
    } else setFormError(prevForm => ({...prevForm, confirm_password: ''}))

    return valid
  }

  const passwordReset = async () => {
    setIsLoading(true)
    try {
      if (!validateForm()) throw Error('Form invalid')
      await post(`Users/updatepassword`, {user_id: userId, password: passForm.password})
      setIsLoading(false)
      setResetSuccess(true)
    } catch (err) {
      setIsLoading(false)
      setFormResMsg('Request failed')
    }
  }

  return (
    <Box className='d-flex flex-column align-items-center justify-content-center' sx={{width: '100vw', height: '100vh'}}>
      {isPrivate &&
        <Typography className='position-absolute' sx={{top: '1rem', left: '1rem'}} variant='caption'>
          Internal
        </Typography>
      }
      {!tokenCheck
        ? <CircularProgress />
        : <Box className='d-flex flex-column align-items-center gap-4 p-5'>
          {!userId
            ? <>
              <Block sx={{fontSize: 100}} color='error' />
              <Typography variant='h5'>Password reset link has been expired, please request again</Typography>
            </>
            : resetSuccess
              ? <>
                <CheckCircleOutline sx={{fontSize: 100}} color='success' />
                <Typography className="text-center" variant='h5'>Password updated</Typography>
                <Button className='fs-5 px-3' variant="contained" href='../login'>Log in</Button>
              </>
              : <Box className='d-flex flex-column align-items-center gap-3 w-100' component="form" sx={{minWidth: 300, maxWidth: 450}}>
                <img src={logo} width={80} className='mb-4' alt="PATHOMIQ"/>
                <Typography className='text-center'>Reset Password</Typography>
                <FormControl sx={{width: '100%'}} variant="outlined" error={!!formError.password} required>
                  <InputLabel htmlFor="login-password">New Password</InputLabel>
                  <OutlinedInput id="login-password" type={showPassword ? 'text' : 'password'} 
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton aria-label={showPassword ? 'hide the password' : 'display the password'}
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          onMouseUp={handleMouseUpPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="New Password"
                    name='password'
                    value={passForm.password}
                    onChange={handleChange}
                  />
                  <FormHelperText error={true}>{formError.password}</FormHelperText>
                </FormControl>
                <FormControl sx={{width: '100%'}} variant="outlined" error={!!formError.confirm_password} required>
                  <InputLabel htmlFor="login-confrim-password">Confirm New Password</InputLabel>
                  <OutlinedInput id="login-confrim-password" type={showConfirmPassword ? 'text' : 'password'} 
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton aria-label={showConfirmPassword ? 'hide the password' : 'display the password'}
                          onClick={handleClickShowConfirmPassword}
                          onMouseDown={handleMouseDownConfirmPassword}
                          onMouseUp={handleMouseUpConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Confirm New Password"
                    name='confirm_password'
                    value={passForm.confirm_password}
                    onChange={handleChange}
                  />
                  <FormHelperText error={true}>{formError.confirm_password}</FormHelperText>
                </FormControl>
                {formResMsg.length ? <Typography color='error'>{formResMsg}</Typography> : null}
                <Button className='p-2 w-100 fs-6' variant="contained" onClick={passwordReset} disabled={isLoading}>
                  {isLoading ? <CircularProgress size={25}/> : 'Save'}
                </Button>
              </Box>
          }
        </Box>
      }
    </Box>
  )
}

export default PasswordReset
